/**
 * The one client-side filter matcher, shared by the list view
 * (examples/Tables/DataTable) and the Kanban views (hooks/useKanbanFilters),
 * so the same filter gives the same rows in both.
 *
 *   applyConditions(rows, { conditions, logic_operator }, fieldMap, objectType)
 *
 * fieldMap is { [fieldName]: { type, reference_to, ... } } from the filter
 * metadata endpoint. Without it every field is compared as text, so callers
 * must wait for the metadata before applying a restored or saved filter.
 */

import { parseServerDateTime } from 'utils/DateTimeConversion';

const isBlank = (v) => v === null || v === undefined || v === '';

// Custom fields are stored as string / int / boolean; the matcher speaks filter types.
const TYPE_ALIASES = {
    string: 'text',
    str: 'text',
    int: 'number',
    integer: 'number',
    float: 'number',
    decimal: 'number',
    percent: 'number',
    percentage: 'number',
    boolean: 'checkbox',
    bool: 'checkbox',
    lookup: 'reference',
};

export const normalizeFieldType = (type) => {
    const t = String(type || 'text').toLowerCase();
    return TYPE_ALIASES[t] || t;
};

// Saved filters created before the field metadata was corrected still carry the
// old field names and the old stage label. Read them under the new names.
const LEGACY_FIELD_ALIASES = {
    deal: { type: 'deal_type' },
    account: { employees: 'number_of_employees', billing_street: 'billing_address', assigned_to: 'manager_id' },
    contact: { mobile: 'mobile_number' },
    invoice: { order_id: 'sales_order_id' },
    case: { source: 'case_source' },
};
const LEGACY_VALUE_ALIASES = {
    deal: { stage: { 'Need Analysis': 'Needs Analysis' } },
};

const SINGULAR_OBJECT_TYPES = {
    leads: 'lead',
    deals: 'deal',
    accounts: 'account',
    contacts: 'contact',
    quotations: 'quotation',
    invoices: 'invoice',
    products: 'product',
    orders: 'order',
    cases: 'case',
};

/** 'deals' -> 'deal'; anything else is returned unchanged. */
export const normalizeObjectType = (objectType) => {
    if (!objectType) return objectType;
    return SINGULAR_OBJECT_TYPES[String(objectType).toLowerCase()] || objectType;
};

export const normalizeCondition = (condition, objectType) => {
    const obj = normalizeObjectType(objectType);
    const field = LEGACY_FIELD_ALIASES[obj]?.[condition.field] || condition.field;
    const valueMap = LEGACY_VALUE_ALIASES[obj]?.[field];
    const value =
        valueMap && typeof condition.value === 'string' && valueMap[condition.value]
            ? valueMap[condition.value]
            : condition.value;
    return field === condition.field && value === condition.value ? condition : { ...condition, field, value };
};

/**
 * Local calendar day (ms) for a date string or Date. "YYYY-MM-DD" is read as a
 * local date; a timestamp without an offset is read as UTC, the way formatDate
 * displays it, so the day matched is the day the user sees.
 */
export const toDay = (v) => {
    if (isBlank(v)) return null;
    const m = typeof v === 'string' && v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    let d;
    if (m) d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    else if (typeof v === 'string') d = parseServerDateTime(v) || new Date(v);
    else d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const readValue = (row, field) => {
    let value = row[field];
    if (value === undefined && field) {
        const key = Object.keys(row).find((k) => k.toLowerCase() === String(field).toLowerCase());
        if (key) value = row[key];
    }
    return value;
};

const asText = (v) => {
    if (isBlank(v)) return '';
    if (Array.isArray(v)) return v.filter((x) => !isBlank(x)).join(', ').trim().toLowerCase();
    return String(v).trim().toLowerCase();
};

export const matchesCondition = (row, { field, operator, value }, fieldMap = {}) => {
    const def = fieldMap[field];
    const type = normalizeFieldType(def?.type);
    let rowValue = readValue(row, field);
    if (Array.isArray(rowValue) && rowValue.length === 0) rowValue = null;
    const rowEmpty = isBlank(rowValue);
    const valueEmpty = isBlank(value);

    if (operator === 'is_empty') return rowEmpty;
    if (operator === 'is_not_empty') return !rowEmpty;

    // Reference fields (assigned_to, account_id, ...): compare ids as strings
    if (type === 'reference') {
        if (rowValue && typeof rowValue === 'object' && rowValue.id !== undefined) rowValue = rowValue.id;
        const r = isBlank(rowValue) ? '' : String(rowValue);
        const v = valueEmpty ? '' : String(value);
        if (operator === 'equals') return r === v;
        if (operator === 'not_equals') return r !== v;
        return true;
    }

    // Dates compare by calendar day, so "equals 2025-03-04" matches any time that day
    if (type === 'date' || type === 'datetime') {
        if (operator === 'between') {
            const r = toDay(rowValue);
            if (r === null || !Array.isArray(value)) return false;
            const lo = toDay(value[0]);
            const hi = toDay(value[1]);
            return (lo === null || r >= lo) && (hi === null || r <= hi);
        }
        const r = toDay(rowValue);
        const v = toDay(value);
        if (r === null || v === null) return false;
        switch (operator) {
            case 'equals': return r === v;
            case 'not_equals': return r !== v;
            case 'greater_than': return r > v;
            case 'greater_than_equal': return r >= v;
            case 'less_than': return r < v;
            case 'less_than_equal': return r <= v;
            default: return true;
        }
    }

    const r = asText(rowValue);
    const v = valueEmpty ? '' : String(value).trim().toLowerCase();
    const isNumeric = type === 'number' || type === 'currency';
    const numeric = (fn) => {
        if (rowEmpty || valueEmpty) return false;
        const a = parseFloat(rowValue);
        const b = parseFloat(value);
        return Number.isNaN(a) || Number.isNaN(b) ? false : fn(a, b);
    };

    switch (operator) {
        case 'equals':
            if (isNumeric && !rowEmpty && !valueEmpty) return numeric((a, b) => a === b);
            return r === v;
        case 'not_equals':
            if (isNumeric && !rowEmpty && !valueEmpty) return !numeric((a, b) => a === b);
            return r !== v;
        case 'contains': return !rowEmpty && r.includes(v);
        case 'does_not_contain': return rowEmpty || !r.includes(v);
        case 'starts_with': return !rowEmpty && r.startsWith(v);
        case 'ends_with': return !rowEmpty && r.endsWith(v);
        case 'greater_than': return numeric((a, b) => a > b);
        case 'greater_than_equal': return numeric((a, b) => a >= b);
        case 'less_than': return numeric((a, b) => a < b);
        case 'less_than_equal': return numeric((a, b) => a <= b);
        case 'between': {
            if (rowEmpty || !Array.isArray(value)) return false;
            const n = parseFloat(rowValue);
            const lo = parseFloat(value[0]);
            const hi = parseFloat(value[1]);
            return (Number.isNaN(lo) || n >= lo) && (Number.isNaN(hi) || n <= hi);
        }
        default:
            return true;
    }
};

/** Rows matching every (AND) or any (OR) condition. No conditions -> rows unchanged. */
export const applyConditions = (rows, applied, fieldMap = {}, objectType) => {
    const list = rows || [];
    const conditions = applied?.conditions;
    if (!conditions || conditions.length === 0) return list;
    const conds = conditions.map((c) => normalizeCondition(c, objectType));
    const matcher = (row) => (c) => matchesCondition(row, c, fieldMap);
    return applied.logic_operator === 'OR'
        ? list.filter((row) => conds.some(matcher(row)))
        : list.filter((row) => conds.every(matcher(row)));
};
